import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Plus, Trash2, AlertTriangle, BarChart2, Star } from 'lucide-react';
import apiService from '../../services/apiService';

/* ── 애니메이션 ── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); }`;
const spin   = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const SpinAnim = styled.span`
  display: inline-flex;
  ${p => p.$active && css`animation: ${spin} 1s linear infinite;`}
`;

/* ── 공통 스타일 ── */
const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0;
  font-family: 'Inter', 'Pretendard', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #1e2030; flex-wrap: wrap;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer; padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1; min-width: 180px;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const PageSub = styled.p`margin: 2px 0 0; font-size: 12px; color: #64748b;`;

const Btn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: 1px solid ${p => p.$primary ? '#f59e0b' : p.$danger ? '#ef4444' : '#374151'};
  background: ${p => p.$primary ? 'rgba(245,158,11,0.12)' : p.$danger ? 'rgba(239,68,68,0.08)' : '#1e2030'};
  color: ${p => p.$primary ? '#fbbf24' : p.$danger ? '#ef4444' : '#94a3b8'};
  &:hover { opacity: 0.85; } &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

/* ── 통계 카드 ── */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
const StatCard  = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 12px; padding: 14px 18px;
`;
const StatNum   = styled.div`font-size: 28px; font-weight: 800; color: ${p => p.$color || '#e2e8f0'};`;
const StatLabel = styled.div`font-size: 11px; color: #64748b; margin-top: 3px;`;

/* ── 수동 추가 폼 ── */
const AddForm = styled.form`
  display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap;
  margin-bottom: 20px; padding: 16px;
  background: #111827; border: 1px solid #1e2030; border-radius: 12px;
`;
const FormGroup = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const Label  = styled.label`font-size: 11px; color: #64748b; font-weight: 600;`;
const Input  = styled.input`
  padding: 8px 10px; background: #0d1117; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px; width: ${p => p.$w || '120px'};
  &:focus { outline: none; border-color: #f59e0b; }
`;
const SelectInForm = styled.select`
  padding: 8px 10px; background: #0d1117; border: 1px solid #374151;
  border-radius: 8px; color: #e2e8f0; font-size: 13px;
  &:focus { outline: none; border-color: #f59e0b; }
`;

/* ── 워치리스트 테이블 ── */
const TableSection = styled.div`
  background: #111827; border: 1px solid #1e2030; border-radius: 14px; overflow: hidden;
`;
const TableHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid #1e2030; background: #0d1117; flex-wrap: wrap;
`;
const TableTitle = styled.span`font-size: 13px; font-weight: 700; color: #e2e8f0;`;
const CountBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 99px;
  background: rgba(245,158,11,0.12); color: #fbbf24;
`;
const OverflowX = styled.div`overflow-x: auto;`;

const COLS = '1fr 76px 90px 82px 80px 70px 50px';
const TH = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 10px 16px; font-size: 10px; color: #64748b; font-weight: 700;
  text-transform: uppercase; border-bottom: 1px solid #1e2030; min-width: 580px;
`;
const TR = styled.div`
  display: grid; grid-template-columns: ${COLS}; gap: 8px;
  padding: 12px 16px; border-bottom: 1px solid #1e2030; font-size: 12px;
  min-width: 580px; transition: background 0.15s; align-items: center;
  animation: ${fadeIn} 0.3s ease;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;

const DayBar = styled.div`
  display: flex; align-items: center; gap: 6px;
`;
const DayBg = styled.div`
  flex: 1; height: 4px; background: #1e2030; border-radius: 2px; overflow: hidden;
`;
const DayFill = styled.div`
  height: 100%; border-radius: 2px;
  width: ${p => p.$pct}%;
  background: ${p => p.$pct < 33 ? '#10b981' : p.$pct < 66 ? '#f59e0b' : '#ef4444'};
  transition: width 0.4s ease;
`;

const MarketTag = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 5px; border-radius: 4px;
  background: ${p => p.$market === 'KOSPI' ? 'rgba(96,165,250,0.12)' : 'rgba(52,211,153,0.12)'};
  color:      ${p => p.$market === 'KOSPI' ? '#60a5fa'               : '#34d399'};
`;
const TypeTag = styled.span`
  font-size: 9px; font-weight: 700; padding: 2px 5px; border-radius: 4px;
  background: ${p => p.$auto ? 'rgba(245,158,11,0.10)' : 'rgba(139,92,246,0.10)'};
  color:      ${p => p.$auto ? '#f59e0b'               : '#8b5cf6'};
`;

const DeleteBtn = styled.button`
  background: none; border: none; color: #475569; cursor: pointer; padding: 4px; border-radius: 6px;
  &:hover { background: rgba(239,68,68,0.12); color: #ef4444; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const EmptyState = styled.div`
  padding: 40px; text-align: center; color: #374151; font-size: 13px;
`;

const AlertBanner = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 14px; margin-bottom: 16px;
  background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.25);
  border-radius: 10px; font-size: 12px; color: #f59e0b;
`;

// ─── 컴포넌트 ─────────────────────────────────────────────────────────────────
function DtlWatchlistPage() {
  const navigate = useNavigate();

  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [refreshMsg,  setRefreshMsg]  = useState(null);
  const [deleting,    setDeleting]    = useState({});

  // 수동 추가 폼
  const [symbol,     setSymbol]     = useState('');
  const [name,       setName]       = useState('');
  const [formMarket, setFormMarket] = useState('KOSPI');
  const [adding,     setAdding]     = useState(false);

  // ── 데이터 로드 ─────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getDtlWatchlist();
      setItems(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── 자동 제외 조건 재평가 ────────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const res = await apiService.refreshDtlWatchlist();
      setRefreshMsg(
        res.excludedCount > 0
          ? `✓ ${res.excludedCount}개 종목이 자동 제외되었습니다.`
          : '✓ 모든 종목이 조건을 충족합니다.'
      );
      await loadList();
    } catch (e) {
      setRefreshMsg(`오류: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // ── 수동 추가 ────────────────────────────────────────────────────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!symbol.trim() || !name.trim()) return alert('종목코드와 종목명을 모두 입력하세요.');
    setAdding(true);
    try {
      await apiService.addDtlWatchlist({ symbol: symbol.trim(), name: name.trim(), market: formMarket });
      setSymbol(''); setName('');
      await loadList();
    } catch (e) {
      alert('추가 실패: ' + e.message);
    } finally {
      setAdding(false);
    }
  };

  // ── 삭제 ─────────────────────────────────────────────────────────────────────
  const handleDelete = async (sym) => {
    if (!window.confirm(`워치리스트에서 ${sym}을(를) 제거하시겠습니까?`)) return;
    setDeleting(p => ({ ...p, [sym]: true }));
    try {
      await apiService.deleteDtlWatchlist(sym);
      await loadList();
    } catch (e) {
      alert('삭제 실패: ' + e.message);
    } finally {
      setDeleting(p => ({ ...p, [sym]: false }));
    }
  };

  // ── 통계 ─────────────────────────────────────────────────────────────────────
  const activeItems  = items.filter(i => i.active);
  const urgentItems  = activeItems.filter(i => (i.remainingTradingDays || 0) <= 7);
  const today        = new Date().toISOString().slice(0, 10);
  const todayItems   = activeItems.filter(i => (i.dDay || '').slice(0, 10) === today);

  const fmt = n => n != null ? Number(n).toLocaleString() : '-';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs/dtl-screener')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>DTL 워치리스트</PageTitle>
          <PageSub>자동 발굴 + 수동 추가 종목 관리 (최대 30 거래일)</PageSub>
        </TitleArea>
        <Btn onClick={() => navigate('/labs/dtl-screener/focus-list')} $primary>
          <Star size={14} /> Focus List
        </Btn>
        <Btn onClick={() => navigate('/labs/dtl-paper')}>
          <BarChart2 size={14} /> 가상매매
        </Btn>
        <Btn $primary onClick={handleRefresh} disabled={refreshing}>
          <SpinAnim $active={refreshing}><RefreshCw size={14} /></SpinAnim>
          {refreshing ? '체크 중...' : '자동제외 체크'}
        </Btn>
      </TopBar>

      {/* ── 결과 메시지 ── */}
      {refreshMsg && (
        <AlertBanner>
          <AlertTriangle size={14} />
          <span>{refreshMsg}</span>
        </AlertBanner>
      )}

      {/* ── 통계 카드 ── */}
      <StatsRow>
        <StatCard>
          <StatNum $color="#fbbf24">{activeItems.length}</StatNum>
          <StatLabel>활성 종목</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum $color={urgentItems.length > 0 ? '#ef4444' : '#64748b'}>{urgentItems.length}</StatNum>
          <StatLabel>만료 임박 (7일 이내)</StatLabel>
        </StatCard>
        <StatCard>
          <StatNum $color="#10b981">{todayItems.length}</StatNum>
          <StatLabel>오늘 신규 등재</StatLabel>
        </StatCard>
      </StatsRow>

      {/* ── 수동 추가 폼 ── */}
      <AddForm onSubmit={handleAdd}>
        <FormGroup>
          <Label>종목코드</Label>
          <Input $w="100px" placeholder="005930" value={symbol} onChange={e => setSymbol(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>종목명</Label>
          <Input $w="150px" placeholder="삼성전자" value={name} onChange={e => setName(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>시장</Label>
          <SelectInForm value={formMarket} onChange={e => setFormMarket(e.target.value)}>
            <option value="KOSPI">KOSPI</option>
            <option value="KOSDAQ">KOSDAQ</option>
          </SelectInForm>
        </FormGroup>
        <Btn $primary type="submit" disabled={adding} style={{ marginTop: 18 }}>
          <Plus size={14} /> {adding ? '추가 중...' : '수동 추가'}
        </Btn>
      </AddForm>

      {/* ── 워치리스트 테이블 ── */}
      <TableSection>
        <TableHeader>
          <TableTitle>워치리스트</TableTitle>
          {activeItems.length > 0 && <CountBadge>{activeItems.length}개</CountBadge>}
          <div style={{ marginLeft: 'auto' }}>
            <Btn onClick={loadList} disabled={loading} style={{ fontSize: 12, padding: '6px 12px' }}>
              <SpinAnim $active={loading}><RefreshCw size={12} /></SpinAnim>
              새로고침
            </Btn>
          </div>
        </TableHeader>

        {loading ? (
          <EmptyState>로딩 중...</EmptyState>
        ) : activeItems.length === 0 ? (
          <EmptyState>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
            <div>워치리스트가 비어 있습니다.</div>
            <div style={{ fontSize: 11, color: '#374151', marginTop: 8 }}>
              스캔을 실행하거나 수동으로 종목을 추가하세요.
            </div>
          </EmptyState>
        ) : (
          <OverflowX>
            <TH>
              <span>종목</span>
              <span>등재일</span>
              <span>잔여거래일</span>
              <span>등재가</span>
              <span>시장</span>
              <span>유형</span>
              <span style={{ textAlign: 'center' }}>삭제</span>
            </TH>
            {activeItems.map(item => {
              const remaining = item.remainingTradingDays ?? 0;
              const pct       = Math.min(100, ((30 - remaining) / 30) * 100);
              return (
                <TR key={item.symbol}>
                  {/* 종목 */}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{item.name}</div>
                    <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{item.symbol}</div>
                  </div>

                  {/* 등재일 */}
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>
                    {(item.dDay || '').slice(5)}
                  </span>

                  {/* 잔여거래일 */}
                  <DayBar>
                    <span style={{
                      fontSize: 12, fontWeight: 700, minWidth: 24,
                      color: remaining <= 7 ? '#ef4444' : remaining <= 14 ? '#f59e0b' : '#10b981'
                    }}>
                      {remaining}일
                    </span>
                    <DayBg><DayFill $pct={pct} /></DayBg>
                  </DayBar>

                  {/* 등재가 */}
                  <span style={{ fontWeight: 600 }}>{fmt(item.dDayClose)}</span>

                  {/* 시장 */}
                  <MarketTag $market={item.market}>{item.market}</MarketTag>

                  {/* 유형 */}
                  <TypeTag $auto={item.addedBy === 'auto'}>
                    {item.addedBy === 'auto' ? '자동' : '수동'}
                  </TypeTag>

                  {/* 삭제 */}
                  <div style={{ textAlign: 'center' }}>
                    <DeleteBtn
                      onClick={() => handleDelete(item.symbol)}
                      disabled={deleting[item.symbol]}
                      title="워치리스트에서 제거"
                    >
                      <Trash2 size={14} />
                    </DeleteBtn>
                  </div>
                </TR>
              );
            })}
          </OverflowX>
        )}
      </TableSection>
    </Page>
  );
}

export default DtlWatchlistPage;
